import { useContext, useEffect, useRef } from 'react';

import { Alert, AlertIcon, Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/macro';
import { useUpdateEffect } from 'usehooks-ts';

import { CoreContext } from '../../../context/CoreContext';
import useCalcAmountToPay from '../../../hooks/useCalcAmountToPay';
import useFetchCheckoutClient from '../../../hooks/useFetchCheckoutClient';
import useFormCheckout from '../../../hooks/useFormCheckout';
import useReadDeposit from '../../../hooks/useReadDeposit';
import useReadManufacturer from '../../../hooks/useReadManufacturer';
import { NO_PAYMENT_METHOD, ONLINE_PAYMENT_METHOD, PAYMENT_LINK_METHOD, PAYMENT_TERMINAL_METHOD } from '../../../hooks/useReadPaymentMethods';
import useReadStripePayment from '../../../hooks/useReadStripePayment';
import { findArrayItemByKey } from '../../../utils/arrayUtil';
import { REQUEST_STATUS } from '../../../utils/requestUtil';
import { COMMON_GAP, ROUNDED_ELEMENT_PROPS } from '../../../utils/styleUtil';
import BorderLeftBox from '../../generic/BorderLeftBox';
import ButtonSubmit from '../../generic/button/ButtonSubmit';
import AlertError from '../../generic/error/AlertError';
import LabelWithText from '../../generic/LabelWithText';
import TeamMail from '../../generic/TeamMail';
import TeamPhone from '../../generic/TeamPhone';
import CheckoutBusinessProvSelect, { BUSINESS_PROV_SELECTOR_FIELDNAME } from './CheckoutBusinessProvSelect';
import CheckoutClientSelectBloc from './CheckoutClientSelectBloc';
import { DEPOSIT_FIELDNAME } from './CheckoutDeposit';
import CheckoutDeposit from './CheckoutDeposit';
import CheckoutFieldsAddress from './CheckoutFieldsAddress';
import CheckoutFieldsCustomer from './CheckoutFieldsCustomer';
import CheckoutGeneralConditions from './CheckoutGeneralConditions';
import CheckoutMailToggle, { SEND_BOOKING_MAIL_FIELDNAME } from './CheckoutMailToggle';
import CheckoutOptIn from './CheckoutOptIn';
import CheckoutPaymentLinkTemplate, { PAYMENTLINK_TEMPLATE_FIELDNAME } from './CheckoutPaymentLinkTemplate';
import CheckoutPaymentMethod, { PAYMENT_METHOD_FIELDNAME } from './CheckoutPaymentMethod';
import CheckoutFieldsExtraData from './form/CheckoutFieldsExtraData';
import CheckoutStripeCard from './stripe/CheckoutStripeCard';

const CheckoutForm = ({
  cartMainClientId,
  totalPrice,
  unavailableItems,
  setUnavailableItems,
  checkoutData,
  checkoutDataStatus,
  getDynamicCheckoutData,
}) => {
  const { coreData } = useContext(CoreContext);
  const {
    team,
    billing_address_required: billingAddressRequired,
    phone_number_required: phoneRequired,
    pre_payment_message: prePaymentMessage,
    business_provider: forcedBusinessProvider,
    booking_engine_specific_data: checkoutExtraData,
    corporate: isCorporateMode,
  } = coreData.booking_engine;
  const loggedUser = coreData.user;

  const { willSearchClients: searchClients, willSearchStaffs: searchStaffs } = useReadManufacturer();

  const { clientSelectorChoices, onClientSelectorSearchChange, isClientSelectorLoading } = useFetchCheckoutClient(searchClients, false, searchStaffs);

  // The pay button is ours, so the stripe api is shared with the form through this ref
  const stripeRef = useRef({ api: null, isRequired: false, isCardComplete: false, hasBillingAddress: false, clientSecret: null });

  const {
    handleSubmit,
    paymentData,
    dataProps,
    isSubmitting,
    status,
    data,
    paymentMethods,
    previousOrderDetails,
    isOrderCreated,
    stripeErrorMessage,
  } = useFormCheckout(cartMainClientId, setUnavailableItems, clientSelectorChoices, searchStaffs, team.currency, totalPrice, stripeRef);

  const { hasDeposit } = useReadDeposit();

  const isTotalAboveZero = totalPrice && parseFloat(totalPrice.tax_included) > 0;
  const amountToPay = useCalcAmountToPay(checkoutData, totalPrice, paymentData[DEPOSIT_FIELDNAME]);
  const { canPayWithStripe, isStripeMisconfigured, stripePromise, clientSecret, brandColors } = useReadStripePayment(
    checkoutData,
    amountToPay,
    isTotalAboveZero
  );

  // We only hand an address over to stripe when our own form guarantees a complete one
  const hasBillingAddress = !isCorporateMode && billingAddressRequired === 1;

  useEffect(() => {
    stripeRef.current.isRequired = canPayWithStripe;
    stripeRef.current.hasBillingAddress = hasBillingAddress;
    stripeRef.current.clientSecret = clientSecret;
  });

  useUpdateEffect(() => {
    const selectedMethodObject = findArrayItemByKey('value', paymentData[PAYMENT_METHOD_FIELDNAME], paymentMethods);
    getDynamicCheckoutData(selectedMethodObject);
  }, [paymentData[PAYMENT_METHOD_FIELDNAME]]);

  const isPaymentLink = paymentData[PAYMENT_METHOD_FIELDNAME] === PAYMENT_LINK_METHOD;
  const isPaymentForm = paymentData[PAYMENT_METHOD_FIELDNAME] === ONLINE_PAYMENT_METHOD;
  const isPaymentNone = paymentData[PAYMENT_METHOD_FIELDNAME] === NO_PAYMENT_METHOD;
  const isPaymentTerminal = paymentData[PAYMENT_METHOD_FIELDNAME] === PAYMENT_TERMINAL_METHOD;

  const canShowGenericError = () => status === REQUEST_STATUS.ERROR;
  const canShowPayBtn = () => !canShowGenericError() && (!data || data.success === true);
  const canShowMailToggle = () => !isCorporateMode && loggedUser && !isPaymentLink && !isPaymentForm && !isPaymentTerminal;
  // A free order has nothing to pay now nor later, a deposit or a "pay on site" choice means nothing
  const canShowDeposit = () => !isCorporateMode && loggedUser == null && hasDeposit && checkoutData != null && isTotalAboveZero;

  // we allow free bookings to pass
  const onlineSetupMisconfig =
    (isTotalAboveZero && paymentMethods.length <= 0) || checkoutDataStatus === REQUEST_STATUS.ERROR || isStripeMisconfigured;
  const isLoading = isSubmitting || checkoutDataStatus === REQUEST_STATUS.LOADING;

  // when logged it's force to not required, otherwise we follow the config
  const isPhoneNumberRequired = loggedUser == null ? phoneRequired : false;

  // Once the order is created only the card can still change, the amount is locked on the payment intent
  const frozenFieldsProps = isOrderCreated ? { opacity: 0.6, pointerEvents: 'none' } : {};

  return (
    <form method="POST" onSubmit={handleSubmit}>
      {!isCorporateMode && loggedUser && (
        <CheckoutClientSelectBloc
          paymentData={paymentData}
          dataProps={dataProps}
          clientSelectorChoices={clientSelectorChoices}
          onClientSelectorSearchChange={onClientSelectorSearchChange}
          isClientSelectorLoading={isClientSelectorLoading}
          previousOrderDetails={previousOrderDetails}
        />
      )}

      <VStack spacing="10px">
        <VStack spacing="10px" w="full" {...frozenFieldsProps}>
          {!isCorporateMode && (
            <CheckoutFieldsCustomer paymentData={paymentData} phoneRequired={isPhoneNumberRequired} loggedUser={loggedUser} {...dataProps} />
          )}
          {!isCorporateMode && billingAddressRequired !== -1 && (
            <CheckoutFieldsAddress paymentData={paymentData} {...dataProps} isRequired={billingAddressRequired === 1} />
          )}
          {!isCorporateMode && checkoutExtraData && (
            <CheckoutFieldsExtraData checkoutExtraData={checkoutExtraData} paymentData={paymentData} {...dataProps} />
          )}

          {!isCorporateMode && loggedUser && (
            <CheckoutPaymentMethod paymentMethods={paymentMethods} fieldValue={paymentData[PAYMENT_METHOD_FIELDNAME]} {...dataProps} />
          )}
          {!isCorporateMode && isPaymentLink && (
            <CheckoutPaymentLinkTemplate fieldValue={paymentData[PAYMENTLINK_TEMPLATE_FIELDNAME]} {...dataProps} />
          )}

          {prePaymentMessage && (
            <>
              <Divider orientation="horizontal" />
              <BorderLeftBox w="full">
                <LabelWithText label={<Trans>Note from the seller</Trans>} text={prePaymentMessage} />
              </BorderLeftBox>
              <Divider orientation="horizontal" />
            </>
          )}

          {!loggedUser && (
            <CheckoutGeneralConditions teamName={team.name} teamCgv={team.cgv} feeIncluded={team.vakario_fee_included} {...dataProps} />
          )}
          {!isCorporateMode && !loggedUser && <CheckoutOptIn {...dataProps} />}

          {!isCorporateMode && loggedUser && !forcedBusinessProvider && (
            <CheckoutBusinessProvSelect fieldValue={paymentData[BUSINESS_PROV_SELECTOR_FIELDNAME]} {...dataProps} />
          )}

          {canShowMailToggle() && <CheckoutMailToggle fieldValue={paymentData[SEND_BOOKING_MAIL_FIELDNAME]} {...dataProps} />}

          {canShowDeposit() && (
            <CheckoutDeposit
              deposit={paymentData[DEPOSIT_FIELDNAME]}
              checkoutData={checkoutData}
              canPayOnSite={!isStripeMisconfigured}
              {...dataProps}
            />
          )}
        </VStack>

        {canPayWithStripe && (
          <CheckoutStripeCard
            stripePromise={stripePromise}
            clientSecret={clientSecret}
            brandColors={brandColors}
            hasBillingAddress={hasBillingAddress}
            stripeRef={stripeRef}
            isDisabled={isSubmitting}
          />
        )}

        {canShowPayBtn() && (
          <ButtonSubmit w="100%" isLoading={isLoading} isDisabled={onlineSetupMisconfig}>
            {!isCorporateMode && !loggedUser && isTotalAboveZero && !canPayWithStripe && <Trans>Pay</Trans>}
            {!isCorporateMode && !loggedUser && isTotalAboveZero && canPayWithStripe && <Trans>Secure my booking</Trans>}
            {!isCorporateMode && !loggedUser && !isTotalAboveZero && <Trans>Create</Trans>}

            {!isCorporateMode && loggedUser && isPaymentLink && <Trans>Send</Trans>}
            {!isCorporateMode && loggedUser && isPaymentNone && <Trans>Create</Trans>}
            {!isCorporateMode && loggedUser && !isPaymentLink && !isPaymentNone && <Trans>Pay</Trans>}

            {isCorporateMode && <Trans>Book</Trans>}
          </ButtonSubmit>
        )}

        {stripeErrorMessage && <AlertError fontSize="sm" message={stripeErrorMessage} />}
        {canShowGenericError() && <AlertError fontSize="sm" message={data ? data.error : undefined} />}
        {unavailableItems && (
          <Alert status="error" {...ROUNDED_ELEMENT_PROPS}>
            <Flex flexDir="column" gap={COMMON_GAP} alignSelf="flex-start" fontSize="xs">
              <HStack>
                <AlertIcon />
                <Text fontSize="sm">
                  <Trans>Some elements of your cart are not available. Please check your cart or contact us.</Trans>
                </Text>
              </HStack>

              <TeamPhone teamPhone={team.phone_number} />
              <TeamMail teamMail={team.email} />
            </Flex>
          </Alert>
        )}
        {onlineSetupMisconfig && <AlertError fontSize="sm" message={<Trans>Online selling is not setup, please contact an administrator.</Trans>} />}
      </VStack>
    </form>
  );
};

export default CheckoutForm;
